import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from 'react';
import { SeverityColors, SeverityColorSet, SeverityLevel, DEFAULT_SEVERITY_COLORS } from '../types';
import api from '../services/api';

interface ColorContextType {
  colors: SeverityColors;
  loading: boolean;
  error: string | null;
  getSeverityColor: (severity: string) => SeverityColorSet;
  getSeverityPrimaryColor: (severity: string) => string;
  refreshColors: () => Promise<void>;
}

const ColorContext = createContext<ColorContextType | undefined>(undefined);

interface ColorProviderProps {
  children: ReactNode;
}

// CSS variable names for severity colors
const CSS_VAR_PREFIX = '--severity';
const STYLE_ELEMENT_ID = 'severity-color-variables';

// Convert hex color to RGB components
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  } : null;
};

// Lighten a hex color by mixing toward white (0 = no change, 1 = white)
const lightenColor = (hex: string, amount: number): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const r = Math.round(rgb.r + (255 - rgb.r) * amount);
  const g = Math.round(rgb.g + (255 - rgb.g) * amount);
  const b = Math.round(rgb.b + (255 - rgb.b) * amount);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// Inject CSS variables via a <style> element with both light and dark theme rules.
// This replaces inline style injection so dark mode gets auto-computed values.
const injectCSSVariables = (colors: SeverityColors) => {
  let styleEl = document.getElementById(STYLE_ELEMENT_ID) as HTMLStyleElement;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = STYLE_ELEMENT_ID;
    document.head.appendChild(styleEl);
  }

  let lightVars = '';
  let darkVars = '';

  Object.entries(colors).forEach(([severity, colorSet]) => {
    // Light mode: use admin-configured values directly
    lightVars += `  ${CSS_VAR_PREFIX}-${severity}-color: ${colorSet.color};\n`;
    lightVars += `  ${CSS_VAR_PREFIX}-${severity}-bg: ${colorSet.bg};\n`;
    lightVars += `  ${CSS_VAR_PREFIX}-${severity}-border: ${colorSet.border};\n`;

    // Dark mode: keep primary color for solid badge backgrounds (white text on colored bg),
    // auto-compute translucent bg/border from primary for outline badges,
    // and add a lightened text variant for text-on-dark-bg usage.
    const rgb = hexToRgb(colorSet.color);
    const darkBg = rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)` : colorSet.bg;
    const darkBorder = rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3)` : colorSet.border;
    // --severity-*-color stays the same (used as solid badge bg with white text)
    // --severity-*-color-text is a lightened version for text on dark backgrounds
    const darkText = lightenColor(colorSet.color, 0.5);

    darkVars += `  ${CSS_VAR_PREFIX}-${severity}-color: ${colorSet.color};\n`;
    darkVars += `  ${CSS_VAR_PREFIX}-${severity}-bg: ${darkBg};\n`;
    darkVars += `  ${CSS_VAR_PREFIX}-${severity}-border: ${darkBorder};\n`;
    darkVars += `  ${CSS_VAR_PREFIX}-${severity}-color-text: ${darkText};\n`;
  });

  // Also set light mode text vars (same as primary color)
  Object.entries(colors).forEach(([severity, colorSet]) => {
    lightVars += `  ${CSS_VAR_PREFIX}-${severity}-color-text: ${colorSet.color};\n`;
  });

  styleEl.textContent = `
:root, [data-theme="light"] {
${lightVars}}
[data-theme="dark"] {
${darkVars}}
`;
};

// Convert SystemSettings color fields to SeverityColors object
const settingsToSeverityColors = (settings: any): SeverityColors => {
  return {
    critical: {
      color: settings.critical_color || DEFAULT_SEVERITY_COLORS.critical.color,
      bg: settings.critical_bg || DEFAULT_SEVERITY_COLORS.critical.bg,
      border: settings.critical_border || DEFAULT_SEVERITY_COLORS.critical.border,
    },
    high: {
      color: settings.high_color || DEFAULT_SEVERITY_COLORS.high.color,
      bg: settings.high_bg || DEFAULT_SEVERITY_COLORS.high.bg,
      border: settings.high_border || DEFAULT_SEVERITY_COLORS.high.border,
    },
    medium: {
      color: settings.medium_color || DEFAULT_SEVERITY_COLORS.medium.color,
      bg: settings.medium_bg || DEFAULT_SEVERITY_COLORS.medium.bg,
      border: settings.medium_border || DEFAULT_SEVERITY_COLORS.medium.border,
    },
    low: {
      color: settings.low_color || DEFAULT_SEVERITY_COLORS.low.color,
      bg: settings.low_bg || DEFAULT_SEVERITY_COLORS.low.bg,
      border: settings.low_border || DEFAULT_SEVERITY_COLORS.low.border,
    },
    info: {
      color: settings.info_color || DEFAULT_SEVERITY_COLORS.info.color,
      bg: settings.info_bg || DEFAULT_SEVERITY_COLORS.info.bg,
      border: settings.info_border || DEFAULT_SEVERITY_COLORS.info.border,
    },
    system: {
      color: settings.system_color || DEFAULT_SEVERITY_COLORS.system.color,
      bg: settings.system_bg || DEFAULT_SEVERITY_COLORS.system.bg,
      border: settings.system_border || DEFAULT_SEVERITY_COLORS.system.border,
    },
  };
};

// Local storage key for caching colors
const COLORS_CACHE_KEY = 'vulnscanner_severity_colors';
const COLORS_CACHE_EXPIRY_KEY = 'vulnscanner_severity_colors_expiry';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

export const ColorProvider: React.FC<ColorProviderProps> = ({ children }) => {
  const [colors, setColors] = useState<SeverityColors>(DEFAULT_SEVERITY_COLORS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load cached colors from localStorage
  const loadCachedColors = useCallback((): SeverityColors | null => {
    try {
      const cached = localStorage.getItem(COLORS_CACHE_KEY);
      const expiry = localStorage.getItem(COLORS_CACHE_EXPIRY_KEY);

      if (cached && expiry) {
        const expiryTime = parseInt(expiry, 10);
        if (Date.now() < expiryTime) {
          return JSON.parse(cached);
        }
      }
    } catch (e) {
      console.warn('Failed to load cached colors:', e);
    }
    return null;
  }, []);

  // Save colors to localStorage cache
  const cacheColors = useCallback((colorsToCache: SeverityColors) => {
    try {
      localStorage.setItem(COLORS_CACHE_KEY, JSON.stringify(colorsToCache));
      localStorage.setItem(COLORS_CACHE_EXPIRY_KEY, String(Date.now() + CACHE_DURATION_MS));
    } catch (e) {
      console.warn('Failed to cache colors:', e);
    }
  }, []);

  // Fetch colors from API (using public endpoint)
  const fetchColors = useCallback(async (): Promise<SeverityColors> => {
    try {
      // Use public colors endpoint (no auth required)
      const response = await api.get('/colors');
      const settings = response.data;
      return settingsToSeverityColors(settings);
    } catch (err) {
      // Silently use defaults - don't log to console
      return DEFAULT_SEVERITY_COLORS;
    }
  }, []);

  // Refresh colors from API
  const refreshColors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const newColors = await fetchColors();
      setColors(newColors);
      injectCSSVariables(newColors);
      cacheColors(newColors);
    } catch (err) {
      setError('Failed to load color settings');
      console.error('Error refreshing colors:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchColors, cacheColors]);

  // Initialize colors on mount
  useEffect(() => {
    const initColors = async () => {
      // First, try to load from cache for immediate display
      const cachedColors = loadCachedColors();
      if (cachedColors) {
        setColors(cachedColors);
        injectCSSVariables(cachedColors);
      } else {
        // Apply defaults immediately
        injectCSSVariables(DEFAULT_SEVERITY_COLORS);
      }

      // Then fetch fresh colors from API
      try {
        const freshColors = await fetchColors();
        setColors(freshColors);
        injectCSSVariables(freshColors);
        cacheColors(freshColors);
      } catch (err) {
        // Silently use cached/defaults
        if (!cachedColors) {
          setColors(DEFAULT_SEVERITY_COLORS);
        }
      } finally {
        setLoading(false);
      }
    };

    initColors();
  }, [loadCachedColors, fetchColors, cacheColors]);

  // Get color set for a severity level
  const getSeverityColor = useCallback((severity: string): SeverityColorSet => {
    const normalizedSeverity = severity.toLowerCase() as SeverityLevel;
    return colors[normalizedSeverity] || colors.system;
  }, [colors]);

  // Get just the primary color for a severity level
  const getSeverityPrimaryColor = useCallback((severity: string): string => {
    return getSeverityColor(severity).color;
  }, [getSeverityColor]);

  const value = useMemo(() => ({
    colors,
    loading,
    error,
    getSeverityColor,
    getSeverityPrimaryColor,
    refreshColors,
  }), [colors, loading, error, getSeverityColor, getSeverityPrimaryColor, refreshColors]);

  return (
    <ColorContext.Provider value={value}>
      {children}
    </ColorContext.Provider>
  );
};

// Hook to use the color context
export const useSeverityColors = (): ColorContextType => {
  const context = useContext(ColorContext);
  if (context === undefined) {
    throw new Error('useSeverityColors must be used within a ColorProvider');
  }
  return context;
};

// Utility hook to get a specific severity's colors
export const useSeverityColor = (severity: string): SeverityColorSet => {
  const { getSeverityColor } = useSeverityColors();
  return getSeverityColor(severity);
};

export default ColorContext;
